import React, { useMemo } from "react";
import { WSMarginProps } from "../WSElement/WSElement.component";
import {
  WSActions,
  WSActionsButton,
  WSActionsMenuItem
} from "../WSActions/WSActions";
import { useCurrentScreenSize } from "../layout";
import sortBy from "lodash/sortBy";
import { WSButtonProps } from "../core/WSButton/WSButton.component";
import { WSIconName } from "../core";
import { WSMenuIconItem, WSMenuItem } from "../WSMenu/WSMenu.component";

export type WSPageToolbarAction = WSActionsMenuItem & {
  loading?: boolean;
  disabled?: boolean;
  buttonKind?: WSButtonProps["kind"];
  buttonLabel?: string;
  buttonIcon?: WSIconName | null;
  hideButtonOn?: WSActionsButton["hideOn"] | false;
  buttonOrder?: number;
  name?: string;
  hidden?: boolean;
  visible?: boolean;
};

export interface WSPageToolbarActionsProps extends WSMarginProps {
  actions?: WSPageToolbarAction[];
  fetchActions?: () => Promise<WSPageToolbarAction[]>;
  actionsLoadingText?: string;
  actionsErrorText?: string;
  onActionsError?: (error: Error) => void;
  className?: string;
}

export const WSPageToolbarActions: React.FC<WSPageToolbarActionsProps> = (
  props
) => {
  const currentScreenSize = useCurrentScreenSize();
  const {
    actions: allActions,
    fetchActions,
    actionsLoadingText,
    actionsErrorText,
    onActionsError,
    ...elementProps
  } = props;

  const actions = useMemo(() => {
    return (allActions || [])
      .filter((action) => action.hidden !== true)
      .filter((action) => action.visible !== false);
  }, [allActions]);

  const menuItems: WSMenuItem[] = useMemo(() => {
    return actions.map(
      ({ buttonKind, buttonLabel, hideButtonOn, buttonOrder, ...menuItem }) =>
        menuItem
    );
  }, [actions]);

  const buttons: WSActionsButton[] = useMemo(() => {
    const actionButtons: WSActionsButton[] = [];

    const filteredActions = actions.filter((action) => action.buttonKind);

    sortBy(filteredActions, ({ buttonOrder = 1 }) => buttonOrder).forEach(
      ({
        buttonKind,
        buttonLabel,
        buttonOrder,
        buttonIcon,
        hideButtonOn = ["XS"],
        ...action
      }) => {
        const buttonProps = action as WSMenuIconItem;

        const sharedProps = {
          onClick: buttonProps.onClick,
          onAsyncClick: action.onAsyncClick,
          loading: action.loading,
          label: buttonProps.label,
          icon: buttonProps.icon,
          kind: buttonKind,
          ...action
        } as WSActionsButton;

        const isVisible = Array.isArray(hideButtonOn)
          ? !hideButtonOn.includes(currentScreenSize)
          : true;

        if (buttonKind && isVisible) {
          actionButtons.push({
            ...sharedProps,
            icon:
              buttonIcon === null ? undefined : buttonIcon || buttonProps.icon,
            label:
              currentScreenSize === "XS" && buttonProps.icon
                ? ""
                : ((buttonLabel ?? buttonProps.label) as string)
          });
        }
      }
    );

    return actionButtons;
  }, [actions, currentScreenSize]);

  const fetchMenuItems = useMemo(() => {
    if (!fetchActions) return undefined;

    return async () => {
      const fetchedActions = await fetchActions();
      return fetchedActions
        .filter((action) => action.hidden !== true)
        .filter((action) => action.visible !== false)
        .map(({ buttonKind, buttonLabel, hideButtonOn, buttonOrder, ...menuItem }) => menuItem);
    };
  }, [fetchActions]);

  return actions.length || fetchMenuItems ? (
    <WSActions
      alignment="right"
      menuItems={menuItems}
      fetchMenuItems={fetchMenuItems}
      menuLoadingText={actionsLoadingText}
      menuErrorText={actionsErrorText}
      onMenuError={onActionsError}
      buttons={buttons}
      role="toolbar"
      aria-label="Page actions"
      {...elementProps}
    />
  ) : null;
};
